import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Space,
  Table,
  App as AntApp,
} from "antd";
import { CopyOutlined, DeleteOutlined, MoreOutlined, PlusOutlined, WarningOutlined } from "@ant-design/icons";

import { usersApi } from "../api/users";
import { maintenanceApi } from "../api/maintenance";
import type { User } from "../api/types";
import { Avatar } from "../components/Avatar";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { useAuthStore } from "../store/auth";
import { formatDateTimeIST, formatRelative } from "../utils/format";

export function AdminUsers() {
  const { notification, modal } = AntApp.useApp();
  const isSuperuser = useAuthStore((s) => s.user?.role === "superuser");
  const [rows, setRows] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [creds, setCreds] = useState<{ email: string; temp: string } | null>(null);
  const [form] = Form.useForm();

  // --- Danger Zone (superuser data wipes) ---
  const [wipeKind, setWipeKind] = useState<"employees" | "inventory" | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [wiping, setWiping] = useState(false);
  const confirmWord = wipeKind === "employees" ? "EMPLOYEES" : "INVENTORY";

  const openWipe = (kind: "employees" | "inventory") => {
    setConfirmText("");
    setWipeKind(kind);
  };
  const closeWipe = () => {
    setWipeKind(null);
    setConfirmText("");
  };

  const runWipe = async () => {
    if (!wipeKind || confirmText.trim().toUpperCase() !== confirmWord) return;
    setWiping(true);
    try {
      const res =
        wipeKind === "employees"
          ? await maintenanceApi.wipeEmployees()
          : await maintenanceApi.wipeInventory();
      const summary = Object.entries(res.deleted)
        .map(([k, v]) => `${v} ${k}`)
        .join(" · ");
      notification.success({ message: res.detail, description: `Removed ${summary}.` });
      closeWipe();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      notification.error({
        message: "Wipe failed",
        description: err.response?.data?.detail || "Could not clear the data.",
      });
    } finally {
      setWiping(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersApi.list({ role: "admin", page_size: 100 });
      setRows(res.results);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onAdd = async () => {
    const vals = await form.validateFields();
    try {
      const created = await usersApi.create({ ...vals, role: "admin" });
      setCreds({
        email: created.email,
        temp: (created as User & { temp_password: string }).temp_password,
      });
      setAddOpen(false);
      form.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, string[]> } };
      notification.error({
        message: err.response?.data ? JSON.stringify(err.response.data) : "Create failed",
      });
    }
  };

  const copy = async () => {
    if (!creds) return;
    await navigator.clipboard.writeText(`Email: ${creds.email}\nPassword: ${creds.temp}`);
    notification.success({ message: "Copied" });
  };

  const onReset = (u: User) => {
    modal.confirm({
      title: `Reset password for ${u.full_name}?`,
      onOk: async () => {
        const r = await usersApi.resetPassword(u.id);
        setCreds({ email: u.email, temp: r.temp_password });
      },
    });
  };

  const onDeactivate = (u: User) => {
    modal.confirm({
      title: `Deactivate ${u.full_name}?`,
      okButtonProps: { danger: true },
      onOk: async () => {
        await usersApi.update(u.id, { is_active: false });
        load();
      },
    });
  };

  const onDelete = (u: User) => {
    modal.confirm({
      title: `Delete ${u.full_name}?`,
      content:
        "This admin account will be removed. The audit log entries they created stay intact. This action cannot be undone from the UI.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await usersApi.remove(u.id);
          notification.success({ message: `${u.full_name} deleted` });
          load();
        } catch (e: unknown) {
          const err = e as { response?: { data?: { detail?: string } } };
          notification.error({
            message: "Delete failed",
            description: err.response?.data?.detail || "Could not delete account.",
          });
        }
      },
    });
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="Admin Users"
        subtitle="Create and manage admin accounts. Admins can manage employees and review orders."
        right={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add Admin
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        message={`${rows.length} admin ${rows.length === 1 ? "account" : "accounts"}.`}
        style={{ marginBottom: 20 }}
      />

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          {
            title: "",
            width: 48,
            render: (_, u: User) => <Avatar name={u.full_name} size={32} />,
          },
          { title: "Full Name", dataIndex: "full_name", render: (v: string) => <b>{v}</b> },
          { title: "Email", dataIndex: "email" },
          {
            title: "Status",
            dataIndex: "is_active",
            render: (v: boolean) => <Pill kind={v ? "active" : "inactive"} />,
          },
          {
            title: "Created",
            dataIndex: "created_at",
            render: (v: string) => <span className="small-muted">{formatDateTimeIST(v)}</span>,
          },
          {
            title: "Last Login",
            dataIndex: "last_login_at",
            render: (v: string | null) => <span className="small-muted">{v ? formatRelative(v) : "Never"}</span>,
          },
          {
            title: "",
            render: (_: unknown, u: User) => (
              <Dropdown
                menu={{
                  items: [
                    { key: "reset", label: "Reset Password", onClick: () => onReset(u) },
                    u.is_active
                      ? { key: "deactivate", label: "Deactivate", danger: true, onClick: () => onDeactivate(u) }
                      : {
                          key: "activate",
                          label: "Activate",
                          onClick: async () => {
                            await usersApi.update(u.id, { is_active: true });
                            load();
                          },
                        },
                    { type: "divider" },
                    {
                      key: "delete",
                      label: "Delete admin",
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(u),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]}
      />

      {isSuperuser && (
        <div className="card" style={{ marginTop: 32, borderColor: "var(--danger-border)", overflow: "hidden" }}>
          <div
            style={{
              padding: "14px 20px",
              background: "var(--danger-soft)",
              borderBottom: "1px solid var(--danger-border)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <WarningOutlined style={{ color: "var(--danger)" }} />
              <span style={{ fontWeight: 600, color: "#b91c1c" }}>Danger Zone</span>
            </div>
            <div className="small-muted" style={{ marginTop: 2 }}>
              Permanent, irreversible actions for clearing data. Superuser only.
            </div>
          </div>
          <DangerRow
            title="Empty employee data"
            desc="Deletes every employee account and all the orders, tasks and notifications they created. Admins & superusers are kept."
            onClick={() => openWipe("employees")}
          />
          <div style={{ height: 1, background: "var(--border)" }} />
          <DangerRow
            title="Empty inventory data"
            desc="Deletes the entire catalog (products & categories) and every order built from it. Employees & shops are kept."
            onClick={() => openWipe("inventory")}
          />
        </div>
      )}

      <Modal title="Create New Admin" open={addOpen} onCancel={() => setAddOpen(false)} onOk={onAdd} okText="Create Admin">
        <Form layout="vertical" form={form} requiredMark={false}>
          <Form.Item label="Full Name" name="full_name" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="e.g. Divya Rajan" />
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ required: true, type: "email" }]}>
            <Input placeholder="divya@example.com" />
          </Form.Item>
          <Form.Item label="Phone (optional)" name="phone">
            <Input placeholder="+91…" />
          </Form.Item>
          <Alert
            type="info"
            showIcon
            message="A temporary password will be generated and shown only once. The admin must change it on first login."
          />
        </Form>
      </Modal>

      <Modal
        title="Admin Created Successfully"
        open={!!creds}
        onCancel={() => setCreds(null)}
        footer={[
          <Button key="done" type="primary" onClick={() => setCreds(null)}>
            Done
          </Button>,
        ]}
      >
        {creds && (
          <div>
            <div
              className="mono"
              style={{ padding: 12, background: "var(--bg-subtle)", borderRadius: 6, fontSize: 13 }}
            >
              <div>Email:    {creds.email}</div>
              <div>Password: {creds.temp}</div>
            </div>
            <Space style={{ marginTop: 12 }}>
              <Button icon={<CopyOutlined />} onClick={copy}>
                Copy Credentials
              </Button>
            </Space>
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message="This password will not be shown again. Copy it now and share securely."
            />
          </div>
        )}
      </Modal>

      <Modal
        open={!!wipeKind}
        title={
          <span style={{ color: "#b91c1c", display: "flex", alignItems: "center", gap: 8 }}>
            <WarningOutlined />
            Permanently delete {wipeKind === "employees" ? "employee" : "inventory"} data?
          </span>
        }
        okText="Permanently delete"
        okButtonProps={{
          danger: true,
          disabled: confirmText.trim().toUpperCase() !== confirmWord,
          loading: wiping,
        }}
        onOk={runWipe}
        onCancel={closeWipe}
        destroyOnClose
      >
        <Alert
          type="error"
          showIcon
          message="This cannot be undone."
          description={
            wipeKind === "employees"
              ? "All employee accounts and the orders, tasks and notifications they created will be permanently deleted. Admins and superusers are kept."
              : "All products and categories, and every order built from them, will be permanently deleted. Employees and shops are kept."
          }
          style={{ marginBottom: 16 }}
        />
        <div style={{ marginBottom: 8, fontSize: 13 }}>
          Type <b className="mono">{confirmWord}</b> to confirm:
        </div>
        <Input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={confirmWord}
          onPressEnter={runWipe}
        />
      </Modal>
    </div>
  );
}

function DangerRow({ title, desc, onClick }: { title: string; desc: string; onClick: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "16px 20px" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, color: "var(--text-primary)" }}>{title}</div>
        <div className="small-muted" style={{ marginTop: 2 }}>{desc}</div>
      </div>
      <Button danger icon={<DeleteOutlined />} onClick={onClick}>
        Delete
      </Button>
    </div>
  );
}
